import React, { useMemo, useState, useContext, createElement, Component } from 'react';
import { useLocation, Link } from 'react-router-dom';
import { FileText, Download, Search, Calendar, Filter, Eye, Settings, ChevronDown, Copy, Check, X, Clock, User, AlertCircle, CheckCircle, XCircle, Activity, ArrowRight, Shield } from 'lucide-react';
import { AppCard } from '../components/base/AppCard';
import { AppButton } from '../components/base/AppButton';
import { AppBadge } from '../components/base/AppBadge';
import { AppInput } from '../components/base/AppInput';
import { Toast } from '../components/Toast';
import { useAgent } from '../contexts/AgentContext';
// Types
interface AuditLog {
  id: string;
  timestamp: string;
  userId: string;
  userName: string;
  userEmail: string;
  serviceId: string;
  serviceName: string;
  eventType: 'Query' | 'Response' | 'Error' | 'Policy Violation' | 'System Event';
  severity: 'Info' | 'Warning' | 'Critical';
  inputPreview: string;
  outputPreview: string;
  duration: number;
  status: 'Success' | 'Failed' | 'Partial';
  tokenUsage: number;
  cost: number;
}
interface LogDetails extends AuditLog {
  fullInput: string;
  fullOutput: string;
  metadata: {
    modelUsed: string;
    toolsInvoked: string[];
    knowledgeSources: string[];
    requestHeaders?: Record<string, string>;
    ip?: string;
    userAgent?: string;
  };
  trace: Array<{
    step: string;
    timestamp: string;
    duration: number;
    status: string;
  }>;
}
// Helper Functions
function isToday(date: Date): boolean {
  const today = new Date();
  return date.getDate() === today.getDate() && date.getMonth() === today.getMonth() && date.getFullYear() === today.getFullYear();
}
function formatDate(timestamp: string): string {
  const date = new Date(timestamp);
  return new Intl.DateTimeFormat('it-IT', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit'
  }).format(date);
}
function convertToCSV(logs: AuditLog[]): string {
  const headers = ['ID', 'Timestamp', 'User', 'Email', 'Service', 'Event Type', 'Status', 'Duration (ms)', 'Tokens', 'Cost'];
  const rows = logs.map(log => [log.id, log.timestamp, log.userName, log.userEmail, log.serviceName, log.eventType, log.status, log.duration, log.tokenUsage, log.cost]);
  return [headers.join(','), ...rows.map(row => row.join(','))].join('\n');
}
function generateMockLogs(): AuditLog[] {
  const logs: AuditLog[] = [];
  const eventTypes: AuditLog['eventType'][] = ['Query', 'Response', 'Error', 'Policy Violation', 'System Event'];
  const severities: AuditLog['severity'][] = ['Info', 'Warning', 'Critical'];
  const statuses: AuditLog['status'][] = ['Success', 'Failed', 'Partial'];
  const services = ['Service 1', 'Service 2', 'Service 3'];
  const users = [{
    name: 'Mario Rossi',
    email: 'mario.rossi@example.com'
  }, {
    name: 'Laura Bianchi',
    email: 'laura.bianchi@example.com'
  }, {
    name: 'Giuseppe Verdi',
    email: 'giuseppe.verdi@example.com'
  }];
  for (let i = 0; i < 150; i++) {
    const user = users[Math.floor(Math.random() * users.length)];
    const service = services[Math.floor(Math.random() * services.length)];
    const eventType = eventTypes[Math.floor(Math.random() * eventTypes.length)];
    const date = new Date();
    date.setHours(date.getHours() - Math.floor(Math.random() * 72));
    logs.push({
      id: `log-${i + 1}`,
      timestamp: date.toISOString(),
      userId: `user-${Math.floor(Math.random() * 3) + 1}`,
      userName: user.name,
      userEmail: user.email,
      serviceId: `service-${Math.floor(Math.random() * 3) + 1}`,
      serviceName: service,
      eventType,
      severity: severities[Math.floor(Math.random() * severities.length)],
      inputPreview: `Query about ${['product info', 'pricing', 'support', 'features'][Math.floor(Math.random() * 4)]}`,
      outputPreview: `Response with ${['detailed info', 'pricing details', 'support guidance', 'feature list'][Math.floor(Math.random() * 4)]}`,
      duration: Math.floor(Math.random() * 5000) + 100,
      status: statuses[Math.floor(Math.random() * statuses.length)],
      tokenUsage: Math.floor(Math.random() * 2000) + 100,
      cost: Math.random() * 0.5
    });
  }
  return logs.sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime());
}
function generateLogDetails(log: AuditLog): LogDetails {
  return {
    ...log,
    fullInput: `Full input text for ${log.inputPreview}. This would contain the complete user query with all context and parameters.`,
    fullOutput: `Full output text for ${log.outputPreview}. This would contain the complete AI response with all details and formatting.`,
    metadata: {
      modelUsed: 'gpt-4-turbo',
      toolsInvoked: ['search', 'calculator', 'database_query'],
      knowledgeSources: ['internal_docs', 'public_api', 'knowledge_base'],
      requestHeaders: {
        'Content-Type': 'application/json',
        Authorization: 'Bearer ***'
      },
      ip: '192.168.1.1',
      userAgent: 'Mozilla/5.0 (Windows NT 10.0; Win64; x64)'
    },
    trace: [{
      step: 'Request Received',
      timestamp: log.timestamp,
      duration: 5,
      status: 'Success'
    }, {
      step: 'Policy Check',
      timestamp: new Date(new Date(log.timestamp).getTime() + 5).toISOString(),
      duration: 15,
      status: 'Success'
    }, {
      step: 'Model Inference',
      timestamp: new Date(new Date(log.timestamp).getTime() + 20).toISOString(),
      duration: log.duration - 50,
      status: 'Success'
    }, {
      step: 'Response Sent',
      timestamp: new Date(new Date(log.timestamp).getTime() + log.duration).toISOString(),
      duration: 10,
      status: 'Success'
    }]
  };
}
// Log Details Modal Component
interface LogDetailsModalProps {
  log: LogDetails;
  activeTab: 'overview' | 'request' | 'response' | 'trace' | 'metadata';
  onTabChange: (tab: 'overview' | 'request' | 'response' | 'trace' | 'metadata') => void;
  onClose: () => void;
}
function LogDetailsModal({
  log,
  activeTab,
  onTabChange,
  onClose
}: LogDetailsModalProps) {
  const [copied, setCopied] = useState(false);
  const handleCopy = (text: string) => {
    navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };
  return <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4" data-id="element-1606" data-mp="1gihhx0ls0o47">
      <div className="bg-[#0a0a0a] rounded-2xl border border-white/[0.08] w-full max-w-4xl max-h-[90vh] overflow-hidden flex flex-col" data-id="element-1607" data-mp="51hqs8b60fbr">
        {/* Header */}
        <div className="px-6 py-5 border-b border-white/[0.06] flex items-center justify-between" data-id="element-1608" data-mp="v52w5m9w1h">
          <div data-id="element-1609" data-mp="2brclf2vssbue">
            <h2 className="text-[17px] font-semibold text-white" data-id="element-1610" data-mp="2bnovseth49ky">
              Log Details
            </h2>
            <p className="text-[12px] text-white/40 mt-1" data-id="element-1611" data-mp="2gmi65ydgrkgw">{log.id}</p>
          </div>
          <button onClick={onClose} className="p-2 text-white\\/40 hover:text-white\\/70 hover:bg-white\\/[0.04] rounded-lg transition-all" data-id="element-1612" data-mp="2llutek88ytk0">
            <X className="w-5 h-5" data-id="element-1613" data-mp="2vd7mff5s7prm" />
          </button>
        </div>

        {/* Tabs */}
        <div className="px-6 border-b border-white/[0.06]" data-id="element-1614" data-mp="2ze1aq9n4z6l1">
          <div className="flex space-x-1" data-id="element-1615" data-mp="34eozsprt1dcm">
            {[{
            id: 'overview',
            label: 'Overview'
          }, {
            id: 'request',
            label: 'Request'
          }, {
            id: 'response',
            label: 'Response'
          }, {
            id: 'trace',
            label: 'Trace'
          }, {
            id: 'metadata',
            label: 'Metadata'
          }].map(tab => <button key={tab.id} onClick={() => onTabChange(tab.id as any)} className={`px-4 py-3 text-[12px] font-medium transition-all relative ${activeTab === tab.id ? 'text-white' : 'text-white/35 hover:text-white/60'}`} data-id="element-1616" data-mp="3bw3wtt33us97">
                {tab.label}
                {activeTab === tab.id && <div className="absolute bottom-0 left-2 right-2 h-[2px] bg-[#8eff01] rounded-full" data-id="element-1617" data-mp="2t0n9wyeoiim8" />}
              </button>)}
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6 custom-scrollbar" data-id="element-1618" data-mp="32vyptk1tpvvk">
          {activeTab === 'overview' && <div className="space-y-6" data-id="element-1619" data-mp="n88f4j4opb0v">
              <div className="grid grid-cols-2 gap-4" data-id="element-1620" data-mp="i7u894etk74f">
                <div data-id="element-1621" data-mp="i95b920fxauh">
                  <p className="text-[10px] text-white/40 mb-1" data-id="element-1622" data-mp="pqtq35mmtqlo">Timestamp</p>
                  <p className="text-[12px] text-white" data-id="element-1623" data-mp="psmwcjtc28t5">
                    {formatDate(log.timestamp)}
                  </p>
                </div>
                <div data-id="element-1624" data-mp="gqndrn2o6wo3">
                  <p className="text-[10px] text-white/40 mb-1" data-id="element-1625" data-mp="grgea6b1cpxs">Duration</p>
                  <p className="text-[12px] text-white" data-id="element-1626" data-mp="kqpbjl5ab5y3">{log.duration}ms</p>
                </div>
                <div data-id="element-1627" data-mp="18grxrkmiphos">
                  <p className="text-[10px] text-white/40 mb-1" data-id="element-1628" data-mp="18hl8ewolkrc7">User</p>
                  <p className="text-[12px] text-white" data-id="element-1629" data-mp="1dgec5xe313z6">{log.userName}</p>
                  <p className="text-[10px] text-white/40" data-id="element-1630" data-mp="zoq362z4qvmf">{log.userEmail}</p>
                </div>
                <div data-id="element-1631" data-mp="13qbgtqjhndxo">
                  <p className="text-[10px] text-white/40 mb-1" data-id="element-1632" data-mp="13pjoqpp3nyc5">Service</p>
                  <p className="text-[12px] text-white" data-id="element-1633" data-mp="1rfxfmcvy7r55">{log.serviceName}</p>
                </div>
                <div data-id="element-1634" data-mp="1w6mc66mhjeb5">
                  <p className="text-[10px] text-white/40 mb-1" data-id="element-1635" data-mp="1w6vztnk259do">Event Type</p>
                  <AppBadge variant="blue" data-id="element-1636" data-mp="3hbriczdfcggw">{log.eventType}</AppBadge>
                </div>
                <div data-id="element-1637" data-mp="3makx0v4g6din">
                  <p className="text-[10px] text-white/40 mb-1" data-id="element-1638" data-mp="3mb4g4f5u37y9">Status</p>
                  <AppBadge variant={log.status === 'Success' ? 'green' : log.status === 'Failed' ? 'red' : 'amber'} data-id="element-1639" data-mp="37dc3buro8xnr">
                    {log.status}
                  </AppBadge>
                </div>
                <div data-id="element-1640" data-mp="33dtj717ocqlz">
                  <p className="text-[10px] text-white/40 mb-1" data-id="element-1641" data-mp="2on2qlqy9dnht">Token Usage</p>
                  <p className="text-[12px] text-white" data-id="element-1642" data-mp="2okzqge3e6dzd">{log.tokenUsage}</p>
                </div>
                <div data-id="element-1643" data-mp="2tjt2e2315wxf">
                  <p className="text-[10px] text-white/40 mb-1" data-id="element-1644" data-mp="2fmeckw8v7n4x">Cost</p>
                  <p className="text-[12px] text-white" data-id="element-1645" data-mp="2fo8x49g85d8h">
                    ${log.cost.toFixed(4)}
                  </p>
                </div>
              </div>
            </div>}

          {activeTab === 'request' && <div data-id="element-1646" data-mp="1w0madgm3vftq">
              <div className="flex items-center justify-between mb-4" data-id="element-1647" data-mp="1w149cl05k85c">
                <h3 className="text-[12px] font-semibold text-white/70" data-id="element-1648" data-mp="29zvomaqr3zv2">
                  Full Request
                </h3>
                <button onClick={() => handleCopy(log.fullInput)} className="flex items-center gap-2 px-3 py-1.5 text-[10px] text-white\\/40 hover:text-white\\/70 bg-white\\/[0.03] hover:bg-white\\/[0.06] rounded-lg transition-all" data-id="element-1649" data-mp="250rcpccfzo11">
                  {copied ? <>
                      <Check className="w-3 h-3" data-id="element-1650" data-mp="2ssprw7f0i3jg" />
                      Copied
                    </> : <>
                      <Copy className="w-3 h-3" data-id="element-1651" data-mp="32nzxf54mct1z" />
                      Copy
                    </>}
                </button>
              </div>
              <div className="bg-white/[0.03] rounded-lg p-4 border border-white/[0.08]" data-id="element-1652" data-mp="2xnvdf54i5g70">
                <pre className="text-[12px] text-white/70 whitespace-pre-wrap font-mono" data-id="element-1653" data-mp="1va7ccyr5naqz">
                  {log.fullInput}
                </pre>
              </div>
            </div>}

          {activeTab === 'response' && <div data-id="element-1654" data-mp="2948dfz5kx9ui">
              <div className="flex items-center justify-between mb-4" data-id="element-1655" data-mp="294qdz7z9ya88">
                <h3 className="text-[12px] font-semibold text-white/70" data-id="element-1656" data-mp="2e3b5mj0gnll2">
                  Full Response
                </h3>
                <button onClick={() => handleCopy(log.fullOutput)} className="flex items-center gap-2 px-3 py-1.5 text-[10px] text-white\\/40 hover:text-white\\/70 bg-white\\/[0.03] hover:bg-white\\/[0.06] rounded-lg transition-all" data-id="element-1657" data-mp="2j0tj8mpfaoze">
                  {copied ? <>
                      <Check className="w-3 h-3" data-id="element-1658" data-mp="srt82dt1t354" />
                      Copied
                    </> : <>
                      <Copy className="w-3 h-3" data-id="element-1659" data-mp="q6296fe7jrq6" />
                      Copy
                    </>}
                </button>
              </div>
              <div className="bg-white/[0.03] rounded-lg p-4 border border-white/[0.08]" data-id="element-1660" data-mp="gf7dkkf2nfs2">
                <pre className="text-[12px] text-white/70 whitespace-pre-wrap font-mono" data-id="element-1661" data-mp="gcuzwjgcjrkt">
                  {log.fullOutput}
                </pre>
              </div>
            </div>}

          {activeTab === 'trace' && <div className="space-y-3" data-id="element-1662" data-mp="144ihubvmrsay">
              {log.trace.map((step, index) => <div key={index} className="flex items-start gap-4 p-4 bg-white/[0.03] rounded-lg border border-white/[0.08]" data-id="element-1663" data-mp="z55s9jddk74o" data-mpi={index}>
                  <div className="flex-shrink-0" data-id="element-1664" data-mp="3jvxuvvg3tf92">
                    {step.status === 'Success' ? <CheckCircle className="w-5 h-5 text-[#8eff01]" data-id="element-1665" data-mp="3fukptnku5g37" /> : <XCircle className="w-5 h-5 text-rose-400" data-id="element-1666" data-mp="17lwhf4bfep4a" />}
                  </div>
                  <div className="flex-1" data-id="element-1667" data-mp="1ccv0gbnsu1b8">
                    <div className="flex items-center justify-between mb-2" data-id="element-1668" data-mp="1cdnxgkwyhcg6">
                      <h4 className="text-[12px] font-medium text-white" data-id="element-1669" data-mp="xoi43dksugg7">
                        {step.step}
                      </h4>
                      <span className="text-[10px] text-white/40" data-id="element-1670" data-mp="12kgx5eh2ommy">
                        {step.duration}ms
                      </span>
                    </div>
                    <p className="text-[10px] text-white/40" data-id="element-1671" data-mp="1v9w6lzt3a7gr">
                      {formatDate(step.timestamp)}
                    </p>
                  </div>
                </div>)}
            </div>}

          {activeTab === 'metadata' && <div className="space-y-6" data-id="element-1672" data-mp="5vrdpjpsrlyv">
              <div data-id="element-1673" data-mp="5upq6frlh709">
                <h3 className="text-[12px] font-semibold text-white/70 mb-3" data-id="element-1674" data-mp="5ydm1lkii5i5">
                  Model Information
                </h3>
                <div className="bg-white/[0.03] rounded-lg p-4 border border-white/[0.08]" data-id="element-1675" data-mp="2dtwa2nn0kl6">
                  <p className="text-[12px] text-white" data-id="element-1676" data-mp="3tkwlwpntuita">
                    {log.metadata.modelUsed}
                  </p>
                </div>
              </div>

              <div data-id="element-1677" data-mp="cagfyo4895lm">
                <h3 className="text-[12px] font-semibold text-white/70 mb-3" data-id="element-1678" data-mp="7dg9ic3gu0tz">
                  Tools Invoked
                </h3>
                <div className="flex flex-wrap gap-2" data-id="element-1679" data-mp="3fsgi3mn3qgrb">
                  {log.metadata.toolsInvoked.map(tool => <AppBadge key={tool} variant="blue" data-id="element-1680" data-mp="3avhlmxfytxpy">
                      {tool}
                    </AppBadge>)}
                </div>
              </div>

              <div data-id="element-1681" data-mp="3ks2rtm3q1evd">
                <h3 className="text-[12px] font-semibold text-white/70 mb-3" data-id="element-1682" data-mp="3krkqib27cagl">
                  Knowledge Sources
                </h3>
                <div className="flex flex-wrap gap-2" data-id="element-1683" data-mp="2x0f8bp8sh634">
                  {log.metadata.knowledgeSources.map(source => <AppBadge key={source} variant="default" data-id="element-1684" data-mp="3hs2qi7xxfqel">
                      {source}
                    </AppBadge>)}
                </div>
              </div>

              {log.metadata.ip && <div data-id="element-1685" data-mp="3ql5w5u2byyyo">
                  <h3 className="text-[12px] font-semibold text-white/70 mb-3" data-id="element-1686" data-mp="3qko3f7evn2vi">
                    Request Info
                  </h3>
                  <div className="space-y-2" data-id="element-1687" data-mp="2ysyhgz6dxwmx">
                    <div className="flex items-center justify-between p-3 bg-white/[0.03] rounded-lg border border-white/[0.08]" data-id="element-1688" data-mp="2yu9vrsxgimsa">
                      <span className="text-[12px] text-white/40" data-id="element-1689" data-mp="32wmp1hv0os86">
                        IP Address
                      </span>
                      <span className="text-[12px] text-white" data-id="element-1690" data-mp="3csgc3v6d4447">
                        {log.metadata.ip}
                      </span>
                    </div>
                    {log.metadata.userAgent && <div className="p-3 bg-white/[0.03] rounded-lg border border-white/[0.08]" data-id="element-1691" data-mp="s1z9gtiazovs">
                        <p className="text-[12px] text-white/40 mb-1" data-id="element-1692" data-mp="2cl2rl0win052">
                          User Agent
                        </p>
                        <p className="text-[10px] text-white/70 break-all" data-id="element-1693" data-mp="27lyj6chf16fs">
                          {log.metadata.userAgent}
                        </p>
                      </div>}
                  </div>
                </div>}
            </div>}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-white/[0.06] flex justify-end" data-id="element-1694" data-mp="1trp57k6vc212">
          <AppButton variant="secondary" onClick={onClose} data-id="element-1695" data-mp="1otnmx1h9o6qn">
            Close
          </AppButton>
        </div>
      </div>
    </div>;
}
// Retention Policy Modal Component
interface RetentionPolicyModalProps {
  onClose: () => void;
  onSave: () => void;
}
function RetentionPolicyModal({
  onClose,
  onSave
}: RetentionPolicyModalProps) {
  const [retentionDays, setRetentionDays] = useState('90');
  const [autoArchive, setAutoArchive] = useState(true);
  const [archiveLocation, setArchiveLocation] = useState('s3');
  const handleSave = () => {
    onSave();
  };
  return <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4" data-id="element-1696" data-mp="2n3hrm16xva26">
      <div className="bg-[#0a0a0a] rounded-2xl border border-white/[0.08] w-full max-w-2xl" data-id="element-1697" data-mp="29cctx4n4sruh">
        {/* Header */}
        <div className="px-6 py-5 border-b border-white/[0.06] flex items-center justify-between" data-id="element-1698" data-mp="2dcwvqlingjmy">
          <div data-id="element-1699" data-mp="1zdndlgavvfgc">
            <h2 className="text-[17px] font-semibold text-white" data-id="element-1700" data-mp="1zhb6cd8i7xu0">
              Retention Policy
            </h2>
            <p className="text-[12px] text-white/40 mt-1" data-id="element-1701" data-mp="1qejtc8zzcu09">
              Configura la policy di conservazione dei log
            </p>
          </div>
          <button onClick={onClose} className="p-2 text-white\\/40 hover:text-white\\/70 hover:bg-white\\/[0.04] rounded-lg transition-all" data-id="element-1702" data-mp="1ugdroampdlx9">
            <X className="w-5 h-5" data-id="element-1703" data-mp="3ou68kp79wdfa" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 space-y-6" data-id="element-1704" data-mp="1d5rqfpseex26">
          <div data-id="element-1705" data-mp="1d6szxptrnbhs">
            <label className="block text-[12px] font-medium text-white/70 mb-2" data-id="element-1706" data-mp="1d46txumix41o">
              Periodo di Conservazione (giorni)
            </label>
            <AppInput type="number" value={retentionDays} onChange={e => setRetentionDays(e.target.value)} placeholder="90" data-id="element-1707" data-mp="1mxe8o635lwef" />
            <p className="text-[10px] text-white/40 mt-2" data-id="element-1708" data-mp="1wtqyutzhps4f">
              I log più vecchi di questo periodo verranno automaticamente
              archiviati
            </p>
          </div>

          <div className="flex items-center justify-between p-4 bg-white/[0.03] rounded-lg border border-white/[0.08]" data-id="element-1709" data-mp="25vgs85cxn14u">
            <div data-id="element-1710" data-mp="20xfapov5n9vh">
              <p className="text-[12px] font-medium text-white" data-id="element-1711" data-mp="20wx3xyav9d9b">
                Archiviazione Automatica
              </p>
              <p className="text-[10px] text-white/40 mt-1" data-id="element-1712" data-mp="bj0nj8zsbv23">
                Archivia automaticamente i log scaduti
              </p>
            </div>
            <label className="relative inline-flex items-center cursor-pointer" data-id="element-1713" data-mp="3axzzgukhrmzs">
              <input type="checkbox" checked={autoArchive} onChange={e => setAutoArchive(e.target.checked)} className="sr-only peer" data-id="element-1714" data-mp="360qbzkgnxlel" />
              <div className="w-11 h-6 bg-white\\/[0.08] peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-\\[#8eff01]\\/20 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-\\[#8eff01]\\" data-id="element-1715" data-mp="3fqt4ljjvgxce"></div>
            </label>
          </div>

          {autoArchive && <div data-id="element-1716" data-mp="g9le1kxce365">
              <label className="block text-[12px] font-medium text-white/70 mb-2" data-id="element-1717" data-mp="g91tdwgb50of">
                Destinazione Archivio
              </label>
              <select value={archiveLocation} onChange={e => setArchiveLocation(e.target.value)} className="w-full bg-white\\/[0.03] border border-white\\/[0.08] text-white px-4 py-2.5 rounded-lg text-[12px] focus:outline-none focus:border-\\[#8eff01]\\/50" data-id="element-1718" data-mp="jsshutwzsoan">
                <option value="s3" data-id="element-1719" data-mp="eso1r4zzxvky">Amazon S3</option>
                <option value="azure" data-id="element-1720" data-mp="evainv8s1nfu">Azure Blob Storage</option>
                <option value="gcs" data-id="element-1721" data-mp="12kcwst47cy5l">Google Cloud Storage</option>
                <option value="local" data-id="element-1722" data-mp="17imubadegaud">Local Storage</option>
              </select>
            </div>}

          <div className="p-4 bg-amber-500/5 border border-amber-500/20 rounded-xl" data-id="element-1723" data-mp="su0e76fy7f23">
            <div className="flex items-start gap-3" data-id="element-1724" data-mp="xrs640glf16a">
              <AlertCircle className="w-5 h-5 text-amber-400 flex-shrink-0 mt-0.5" data-id="element-1725" data-mp="xqh5g9ig29je" />
              <div data-id="element-1726" data-mp="1lgbfkjbdpjzy">
                <p className="text-[12px] font-medium text-white mb-1" data-id="element-1727" data-mp="1lftb4zekbzgy">
                  Attenzione
                </p>
                <p className="text-[12px] text-white/40" data-id="element-1728" data-mp="204l4xxh02rxv">
                  La modifica della retention policy potrebbe influenzare la
                  conformità normativa. Assicurati di rispettare i requisiti
                  GDPR e AI ACT.
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-white/[0.06] flex justify-end gap-3" data-id="element-1729" data-mp="1m4af8xrffjea">
          <AppButton variant="secondary" onClick={onClose} data-id="element-1730" data-mp="1h6ioi72iq3bo">
            Annulla
          </AppButton>
          <AppButton variant="primary" onClick={handleSave} data-id="element-1731" data-mp="1ce8qs4v5vyyu">
            Salva Policy
          </AppButton>
        </div>
      </div>
    </div>;
}
// Main Component
interface AuditTrailPageProps {
  hideHeader?: boolean;
  serviceScope?: string | null;
}
export function AuditTrailPage({
  hideHeader = false,
  serviceScope
}: AuditTrailPageProps) {
  // Context
  const {
    selectedServiceId,
    setSelectedServiceId
  } = useAgent();
  const location = useLocation();
  // Usa serviceScope da prop, oppure da context
  const effectiveServiceScope = serviceScope !== undefined ? serviceScope : selectedServiceId;
  const isServiceScoped = !!effectiveServiceScope;
  // Helper function to get service name
  const getServiceName = (serviceId: string | null) => {
    if (!serviceId) return 'Unknown';
    const serviceMap: Record<string, string> = {
      'service-1': 'Service 1',
      'service-2': 'Service 2',
      'service-3': 'Service 3',
      'service-4': 'Service 4',
      'service-5': 'Service 5'
    };
    return serviceMap[serviceId] || `Service ${serviceId}`;
  };
  // State
  const [allLogs] = useState<AuditLog[]>(generateMockLogs());
  const [selectedLog, setSelectedLog] = useState<LogDetails | null>(null);
  const [showRetentionModal, setShowRetentionModal] = useState(false);
  const [activeDetailTab, setActiveDetailTab] = useState<'overview' | 'request' | 'response' | 'trace' | 'metadata'>('overview');
  const [toast, setToast] = useState<{
    message: string;
    type: 'success' | 'error' | 'info';
  } | null>(null);
  // Filters
  const [searchQuery, setSearchQuery] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [selectedServices, setSelectedServices] = useState<string[]>([]);
  const [selectedUsers, setSelectedUsers] = useState<string[]>([]);
  const [selectedEventType, setSelectedEventType] = useState<string>('All');
  const [selectedSeverity, setSelectedSeverity] = useState<string>('All');
  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const logsPerPage = 50;
  // Filtered logs with SERVICE SCOPE as primary filter
  const filteredLogs = useMemo(() => {
    return allLogs.filter(log => {
      // PRIMARY FILTER: Service scope
      if (isServiceScoped && effectiveServiceScope) {
        if (log.serviceId !== effectiveServiceScope) return false;
      }
      // Secondary filters (esistenti)
      if (searchQuery && !log.inputPreview.toLowerCase().includes(searchQuery.toLowerCase()) && !log.outputPreview.toLowerCase().includes(searchQuery.toLowerCase())) return false;
      if (selectedEventType !== 'All' && log.eventType !== selectedEventType) return false;
      if (selectedSeverity !== 'All' && log.severity !== selectedSeverity) return false;
      if (selectedServices.length > 0 && !selectedServices.includes(log.serviceId)) return false;
      if (selectedUsers.length > 0 && !selectedUsers.includes(log.userId)) return false;
      return true;
    });
  }, [allLogs, isServiceScoped, effectiveServiceScope, searchQuery, selectedEventType, selectedSeverity, selectedServices, selectedUsers]);
  // Paginated logs
  const paginatedLogs = useMemo(() => {
    const startIndex = (currentPage - 1) * logsPerPage;
    return filteredLogs.slice(startIndex, startIndex + logsPerPage);
  }, [filteredLogs, currentPage]);
  // Stats
  const stats = useMemo(() => ({
    totalLogs: filteredLogs.length,
    todayActivity: filteredLogs.filter(l => isToday(new Date(l.timestamp))).length,
    avgResponseTime: filteredLogs.length > 0 ? (filteredLogs.reduce((sum, l) => sum + l.duration, 0) / filteredLogs.length / 1000).toFixed(1) : '0.0',
    complianceScore: 98
  }), [filteredLogs]);
  // Handlers
  const handleExport = (format: 'csv' | 'json') => {
    const dataStr = format === 'json' ? JSON.stringify(filteredLogs, null, 2) : convertToCSV(filteredLogs);
    const blob = new Blob([dataStr], {
      type: format === 'json' ? 'application/json' : 'text/csv'
    });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `audit-trail-${new Date().toISOString().split('T')[0]}.${format}`;
    link.click();
    setToast({
      message: `Export ${format.toUpperCase()} completato`,
      type: 'success'
    });
  };
  const handleViewDetails = (log: AuditLog) => {
    setSelectedLog(generateLogDetails(log));
  };
  const handleResetFilters = () => {
    setSearchQuery('');
    setDateFrom('');
    setDateTo('');
    setSelectedServices([]);
    setSelectedUsers([]);
    setSelectedEventType('All');
    setSelectedSeverity('All');
  };
  return <div className="min-h-screen bg-[#F4F6F8]" data-id="element-1732" data-mp="1pk1aozj9wg0a">
      {/* Toast */}
      {toast && <Toast message={toast.message} type={toast.type} isVisible={!!toast} onClose={() => setToast(null)} data-id="element-1733" data-mp="1uickja9kiqii" />}

      {/* Header */}
      {!hideHeader && <div className="p-4 md:p-8 pb-6" data-id="element-1734" data-mp="248xhkt9lfmzb">
          <h1 className="text-[28px] md:text-[32px] font-semibold text-[#2a3a54] tracking-tight" data-id="element-1735" data-mp="298jq4peh8f1s">
            Audit Trail
          </h1>
        </div>}

      <div className="px-4 md:px-8 space-y-6 pb-12" data-id="element-1736" data-mp="2daohtild71ls">
        {/* Service-Scoped Banner */}
        {isServiceScoped && effectiveServiceScope && <div className="bg-blue-50 border border-blue-100 rounded-xl p-4 flex items-center justify-between" data-id="element-1737" data-mp="2ujs1ogxhhqqw">
            <div className="flex items-center gap-3" data-id="element-1738" data-mp="38jjdcb0j2v1q">
              <div className="p-2 bg-blue-100 rounded-lg" data-id="element-1739" data-mp="38h6vryyommp7">
                <Filter className="w-5 h-5 text-[#00B4D8]" data-id="element-1740" data-mp="33l84o3lyyql3" />
              </div>
              <div data-id="element-1741" data-mp="2fu2lbegtlg61">
                <p className="text-[12px] font-medium text-[#2a3a54]" data-id="element-1742" data-mp="2fvdrtm1nh20u">
                  Viewing: {getServiceName(effectiveServiceScope)}
                </p>
                <p className="text-[10px] text-gray-500 mt-0.5" data-id="element-1743" data-mp="2pkf8zfjxlpge">
                  Showing data for this service only
                </p>
              </div>
            </div>
            <Link to="/audit-trail" onClick={() => setSelectedServiceId(null)} className="flex items-center gap-2 px-4 py-2 text-[12px] font-medium text-[#00B4D8] hover:bg-blue-100 rounded-lg transition-all" data-id="element-1744" data-mp="2krm32ocq8b2q">
              View all services
              <ArrowRight className="w-4 h-4" data-id="element-1745" data-mp="7kw5aac0i4q8" />
            </Link>
          </div>}

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4" data-id="element-1746" data-mp="3urot7wvfzypa">
          <div className="bg-white border border-gray-200 rounded-xl p-5 shadow-sm flex flex-col" data-id="element-1747" data-mp="3ut9k9cijszie">
            <h3 className="text-[13px] font-medium text-gray-500 mb-3" data-id="element-1748" data-mp="2oflfsbuiciy">
              Total Logs
            </h3>
            <div className="flex items-baseline gap-2 mt-auto" data-id="element-1749" data-mp="qbdui4xmyi6u">
              <span className="text-[28px] font-bold text-[#1A202C]" data-id="element-1750" data-mp="vba0uatt2lpk">
                {stats.totalLogs.toLocaleString()}
              </span>
            </div>
          </div>

          <div className="bg-white border border-gray-200 rounded-xl p-5 shadow-sm flex flex-col" data-id="element-1751" data-mp="mbdggleg890b">
            <h3 className="text-[13px] font-medium text-gray-500 mb-3" data-id="element-1752" data-mp="322huoxvzn941">
              Today's Activity
            </h3>
            <div className="flex items-baseline gap-2 mt-auto" data-id="element-1753" data-mp="3735n9nzv7y0d">
              <span className="text-[28px] font-bold text-[#1A202C]" data-id="element-1754" data-mp="2554aneemshwh">
                {stats.todayActivity}
              </span>
            </div>
          </div>

          <div className="bg-white border border-gray-200 rounded-xl p-5 shadow-sm flex flex-col" data-id="element-1755" data-mp="2a248x358ogd1">
            <h3 className="text-[13px] font-medium text-gray-500 mb-3" data-id="element-1756" data-mp="2f3tmoqjuj84t">
              Avg Response Time
            </h3>
            <div className="flex items-baseline gap-2 mt-auto" data-id="element-1757" data-mp="1mecunu051t94">
              <span className="text-[28px] font-bold text-[#1A202C]" data-id="element-1758" data-mp="1mfxqdmy6vi8k">
                {stats.avgResponseTime}s
              </span>
            </div>
          </div>

          <div className="bg-white border border-gray-200 rounded-xl p-5 shadow-sm flex flex-col" data-id="element-1759" data-mp="1vesv0ull4k9r">
            <h3 className="text-[13px] font-medium text-gray-500 mb-3" data-id="element-1760" data-mp="206157suqo9nz">
              Compliance Score
            </h3>
            <div className="flex items-baseline gap-2 mt-auto" data-id="element-1761" data-mp="13n3lxmy6z8il">
              <span className="text-[28px] font-bold text-[#00B4D8]" data-id="element-1762" data-mp="17oyk4iktt5s5">
                {stats.complianceScore}%
              </span>
            </div>
          </div>
        </div>

        {/* Compact Filters */}
        <div className="bg-white border border-gray-200 rounded-xl p-4 shadow-sm flex flex-col md:flex-row gap-4 items-center justify-between" data-id="element-1763" data-mp="v6mftxq20h8u">
          <div className="flex flex-1 items-center gap-4 w-full md:w-auto" data-id="element-1764" data-mp="r3g3c8cclttx">
            <div className="relative flex-1 md:max-w-xs" data-id="element-1765" data-mp="m5ejhl73lqhe">
              <Search className="w-4 h-4 text-gray-400 absolute left-3 top-3" data-id="element-1766" data-mp="h6tivknxp4sf" />
              <input type="text" placeholder="Cerca..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} className="w-full bg-gray-50 border border-gray-200 text-[#1A202C] pl-9 pr-4 py-2.5 rounded-lg text-[12px] placeholder-gray-400 focus:outline-none focus:border-[#00B4D8] focus:ring-1 focus:ring-[#00B4D8]" data-id="element-1767" data-mp="h8xxawjglm6y" />
            </div>

            <select value={selectedEventType} onChange={e => setSelectedEventType(e.target.value)} className="bg-gray-50 border border-gray-200 text-[#1A202C] px-4 py-2.5 rounded-lg text-[12px] focus:outline-none focus:border-[#00B4D8] focus:ring-1 focus:ring-[#00B4D8]" data-id="element-1768" data-mp="3pny1luw2bfyh">
              <option data-id="element-1769" data-mp="362xuloyzn4d1">All Events</option>
              <option data-id="element-1770" data-mp="3azxypuca1x1i">Query</option>
              <option data-id="element-1771" data-mp="3b0zci6kogxmx">Response</option>
              <option data-id="element-1772" data-mp="3b210tcqpfgml">Error</option>
              <option data-id="element-1773" data-mp="2juuxzoacsig">Policy Violation</option>
              <option data-id="element-1774" data-mp="2l65cic8kyij">System Event</option>
            </select>

            <select value={selectedSeverity} onChange={e => setSelectedSeverity(e.target.value)} className="bg-gray-50 border border-gray-200 text-[#1A202C] px-4 py-2.5 rounded-lg text-[12px] focus:outline-none focus:border-[#00B4D8] focus:ring-1 focus:ring-[#00B4D8]" data-id="element-1775" data-mp="7ifstsg2f03q">
              <option data-id="element-1776" data-mp="lhxn2nc4r5kh">All Severities</option>
              <option data-id="element-1777" data-mp="q9p5ubp1o702">Info</option>
              <option data-id="element-1778" data-mp="qaqsldffahxm">Warning</option>
              <option data-id="element-1779" data-mp="qbthxgx3kcbf">Critical</option>
            </select>
          </div>

          <div className="flex gap-2" data-id="element-1780" data-mp="2ayt78hkpbjbo">
            <button onClick={() => handleExport('csv')} title="Export CSV" className="p-2 text-gray-600 hover:text-[#1A202C] hover:bg-gray-100 rounded-lg transition-all" data-id="element-1781" data-mp="261ayar6op43o">
              <Download className="w-4 h-4" data-id="element-1782" data-mp="2fquflser0pwf" />
            </button>
            <button onClick={handleResetFilters} title="Reset Filters" className="p-2 text-gray-600 hover:text-[#1A202C] hover:bg-gray-100 rounded-lg transition-all" data-id="element-1783" data-mp="1s1i8qz6w8txa">
              <X className="w-4 h-4" data-id="element-1784" data-mp="212pvql3ym8c2" />
            </button>
          </div>
        </div>

        {/* Audit Table */}
        <div className="bg-white border border-gray-200 rounded-xl shadow-sm overflow-hidden" data-id="element-1785" data-mp="1wzjp3ccxqf52">
          <div className="px-6 py-5 border-b border-gray-100 flex items-center justify-between" data-id="element-1786" data-mp="3cgbt2tjz3g8o">
            <h2 className="text-[16px] font-semibold text-[#2a3a54]" data-id="element-1787" data-mp="38eq8sn53zzog">
              Audit Log
            </h2>
            <button onClick={() => handleExport('csv')} className="text-[13px] text-gray-500 font-medium hover:text-[#2a3a54] transition-colors" data-id="element-1788" data-mp="7lvjj0ftfdrb">
              Esporta Report
            </button>
          </div>

          {/* Desktop Table */}
          <div className="hidden md:block overflow-x-auto" data-id="element-1789" data-mp="lklea9f5x1ci">
            <table className="w-full" data-id="element-1790" data-mp="qjejzfo7jo24">
              <thead className="bg-gray-50/50 border-b border-gray-100" data-id="element-1791" data-mp="qid8ja3a4zjv">
                <tr data-id="element-1792" data-mp="ukrrrvav21bo">
                  <th className="px-6 py-3 text-left text-[11px] font-semibold text-gray-500 uppercase tracking-wider" data-id="element-1793" data-mp="uk8bej0uzkzr">
                    Timestamp
                  </th>
                  <th className="px-6 py-3 text-left text-[11px] font-semibold text-gray-500 uppercase tracking-wider" data-id="element-1794" data-mp="14ajpgye9bakd">
                    User
                  </th>
                  <th className="px-6 py-3 text-left text-[11px] font-semibold text-gray-500 uppercase tracking-wider" data-id="element-1795" data-mp="197tam1um502e">
                    Service
                  </th>
                  <th className="px-6 py-3 text-left text-[11px] font-semibold text-gray-500 uppercase tracking-wider" data-id="element-1796" data-mp="2yrt417viazr4">
                    Event Type
                  </th>
                  <th className="px-6 py-3 text-left text-[11px] font-semibold text-gray-500 uppercase tracking-wider" data-id="element-1797" data-mp="2osc4772xrg32">
                    Status
                  </th>
                  <th className="px-6 py-3 text-left text-[11px] font-semibold text-gray-500 uppercase tracking-wider" data-id="element-1798" data-mp="3hhjbaugghdzx">
                    Duration
                  </th>
                  <th className="px-6 py-3 text-right text-[11px] font-semibold text-gray-500 uppercase tracking-wider" data-id="element-1799" data-mp="3cj80agefcxg4">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100" data-id="element-1800" data-mp="32qk5fr7093k1">
                {paginatedLogs.map(log => <tr key={log.id} className="hover:bg-gray-50/50 transition-colors" data-id="element-1801" data-mp="44hcmf0z7yhm">
                    <td className="px-6 py-4 whitespace-nowrap text-[13px] text-gray-500" data-id="element-1802" data-mp="1tktakgcv71oz">
                      {formatDate(log.timestamp)}
                    </td>
                    <td className="px-6 py-4" data-id="element-1803" data-mp="1ju8fh2wegfat">
                      <div data-id="element-1804" data-mp="1jt5m8oblu0rq">
                        <p className="text-[13px] font-medium text-[#2a3a54]" data-id="element-1805" data-mp="1jrujmrtwaz27">
                          {log.userName}
                        </p>
                        <p className="text-[13px] text-gray-500" data-id="element-1806" data-mp="1at0qxcjoqstx">
                          {log.userEmail}
                        </p>
                      </div>
                    </td>
                    <td className="px-6 py-4" data-id="element-1807" data-mp="10vmb9f5l682o">
                      <span className="inline-flex items-center px-2.5 py-1 rounded-full text-[11px] font-medium bg-blue-50 text-blue-700 border border-blue-100" data-id="element-1808" data-mp="10wxf1ez17fts">
                        {log.serviceName}
                      </span>
                    </td>
                    <td className="px-6 py-4" data-id="element-1809" data-mp="308grw4ngh88q">
                      <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-[11px] font-medium border ${log.eventType === 'Query' ? 'bg-blue-50 text-blue-700 border-blue-100' : log.eventType === 'Response' ? 'bg-emerald-50 text-emerald-700 border-emerald-100' : log.eventType === 'Error' || log.eventType === 'Policy Violation' ? 'bg-red-50 text-red-700 border-red-100' : 'bg-gray-50 text-gray-700 border-gray-100'}`} data-id="element-1810" data-mp="30649xrhp6xvo">
                        {log.eventType}
                      </span>
                    </td>
                    <td className="px-6 py-4" data-id="element-1811" data-mp="12bqegngi3e2k">
                      <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-[11px] font-medium border ${log.status === 'Success' ? 'bg-emerald-50 text-emerald-700 border-emerald-100' : log.status === 'Failed' ? 'bg-red-50 text-red-700 border-red-100' : 'bg-amber-50 text-amber-700 border-amber-100'}`} data-id="element-1812" data-mp="1h9921mhfzl8f">
                        {log.status}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-[13px] text-gray-500" data-id="element-1813" data-mp="2s8dahwmgqunk">
                      {log.duration}ms
                    </td>
                    <td className="px-6 py-4 text-right" data-id="element-1814" data-mp="31xwx9ddar8np">
                      <button onClick={() => handleViewDetails(log)} className="text-[13px] font-medium text-[#00B4D8] hover:text-[#0096b4] transition-colors" data-id="element-1815" data-mp="320jbtwyluoff">
                        Dettagli
                      </button>
                    </td>
                  </tr>)}
              </tbody>
            </table>
          </div>

          {/* Mobile Cards */}
          <div className="md:hidden flex flex-col divide-y divide-gray-100" data-id="element-1816" data-mp="2n9skeq0xdrcu">
            {paginatedLogs.map(log => <div key={log.id} className="p-4 flex flex-col gap-3 hover:bg-gray-50/50 transition-colors" data-id="element-1817" data-mp="3umc7znpeb4t8">
                <div className="flex justify-between items-start" data-id="element-1818" data-mp="1n142cdo88bv1">
                  <div data-id="element-1819" data-mp="1n3gfmdj1hy1r">
                    <p className="text-[13px] font-medium text-[#2a3a54]" data-id="element-1820" data-mp="1izqxa5sykcp6">
                      {log.userName}
                    </p>
                    <p className="text-[12px] text-gray-500" data-id="element-1821" data-mp="1e80gbgzimqos">
                      {log.serviceName} • {log.eventType}
                    </p>
                  </div>
                  <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-[10px] font-medium border ${log.status === 'Success' ? 'bg-emerald-50 text-emerald-700 border-emerald-100' : log.status === 'Failed' ? 'bg-red-50 text-red-700 border-red-100' : 'bg-amber-50 text-amber-700 border-amber-100'}`} data-id="element-1822" data-mp="14amccgus3udd">
                    {log.status}
                  </span>
                </div>
                <div className="flex justify-between items-center mt-1" data-id="element-1823" data-mp="2pmw4btk1y4i0">
                  <span className="text-[12px] text-gray-500" data-id="element-1824" data-mp="2kokwfodbi42f">
                    {formatDate(log.timestamp)} • {log.duration}ms
                  </span>
                  <button onClick={() => handleViewDetails(log)} className="text-[12px] font-medium text-[#00B4D8]" data-id="element-1825" data-mp="1yqb1hxw42vd2">
                    Dettagli
                  </button>
                </div>
              </div>)}
          </div>

          {/* Pagination */}
          <div className="px-6 py-4 border-t border-gray-100 flex items-center justify-between" data-id="element-1826" data-mp="2him1njzdsf7h">
            <p className="text-[13px] text-gray-500" data-id="element-1827" data-mp="2ck2dre188tch">
              Showing {(currentPage - 1) * logsPerPage + 1}-
              {Math.min(currentPage * logsPerPage, filteredLogs.length)} of{' '}
              {filteredLogs.length} logs
            </p>
            <div className="flex gap-2" data-id="element-1828" data-mp="23qz137yeokiw">
              <button disabled={currentPage === 1} onClick={() => setCurrentPage(p => p - 1)} className="px-3 py-1.5 text-[13px] font-medium text-gray-700 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-all" data-id="element-1829" data-mp="309d8adrzhri6">
                Previous
              </button>
              <button disabled={currentPage * logsPerPage >= filteredLogs.length} onClick={() => setCurrentPage(p => p + 1)} className="px-3 py-1.5 text-[13px] font-medium text-gray-700 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-all" data-id="element-1830" data-mp="r0ouu6uqamib">
                Next
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Log Details Modal */}
      {selectedLog && <LogDetailsModal log={selectedLog} activeTab={activeDetailTab} onTabChange={setActiveDetailTab} onClose={() => setSelectedLog(null)} data-id="element-1831" data-mp="1eqjaew6fmp69" />}

      {/* Retention Policy Modal */}
      {showRetentionModal && <RetentionPolicyModal onClose={() => setShowRetentionModal(false)} onSave={() => {
      setShowRetentionModal(false);
      setToast({
        message: 'Retention policy salvata',
        type: 'success'
      });
    }} data-id="element-1832" data-mp="3krlmyqg3um4o" />}
    </div>;
}